import React from "react";
import {
  LuChevronsLeft,
  LuChevronLeft,
  LuChevronRight,
  LuChevronsRight,
} from "react-icons/lu";

const pageBtnStyle: React.CSSProperties = {
  width: "32px",
  height: "32px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 0,
  border: "1px solid #cbd5e1",
  borderRadius: "6px",
  backgroundColor: "#ffffff",
  color: "#475569",
};

const pageBtnDisabledStyle: React.CSSProperties = {
  ...pageBtnStyle,
  color: "#cbd5e1",
  cursor: "not-allowed",
  backgroundColor: "#f8fafc",
};

function PageNavButton({
  onClick,
  disabled,
  label,
  children,
}: {
  onClick: () => void;
  disabled: boolean;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      className="page-nav-btn"
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      style={disabled ? pageBtnDisabledStyle : pageBtnStyle}
    >
      {children}
    </button>
  );
}

export interface PaginationControlsProps {
  currentPage: number;
  totalPages: number;
  pageSize: number;
  totalCount: number;
  pageInput: string;
  onPageInputChange: (value: string) => void;
  onCommitPageInput: () => void;
  onFirst: () => void;
  onPrevious: () => void;
  onNext: () => void;
  onLast: () => void;
}

export function PaginationControls({
  currentPage,
  totalPages,
  pageSize,
  totalCount,
  pageInput,
  onPageInputChange,
  onCommitPageInput,
  onFirst,
  onPrevious,
  onNext,
  onLast,
}: PaginationControlsProps) {
  if (totalCount === 0) return null;
  return (
    <div
      className="report-filter-no-print d-flex align-items-center justify-content-between flex-wrap gap-2"
      style={{
        padding: "14px 20px",
        backgroundColor: "#f8fafc",
        borderTop: "1px solid #dee2e6",
      }}
    >
      <style>{`
        .page-nav-btn {
          transition: background-color 0.15s ease, border-color 0.15s ease;
        }
        .page-nav-btn:hover:not(:disabled) {
          background-color: #eef2f6;
          border-color: #94a3b8;
        }
        .page-nav-btn:focus-visible {
          outline: 2px solid var(--color-p2);
          outline-offset: 2px;
        }
      `}</style>
      <span style={{ fontSize: "0.8125rem", color: "#475569" }}>
        Showing{" "}
        <strong style={{ color: "#0f1923" }}>
          {((currentPage - 1) * pageSize + 1).toLocaleString()}
        </strong>
        {" – "}
        <strong style={{ color: "#0f1923" }}>
          {Math.min(currentPage * pageSize, totalCount).toLocaleString()}
        </strong>{" "}
        of{" "}
        <strong style={{ color: "#0f1923" }}>
          {totalCount.toLocaleString()}
        </strong>{" "}
        {totalCount === 1 ? "record" : "records"}
      </span>

      <div className="d-flex align-items-center gap-2">
        <PageNavButton
          label="First page"
          disabled={currentPage <= 1}
          onClick={onFirst}
        >
          <LuChevronsLeft size={16} />
        </PageNavButton>
        <PageNavButton
          label="Previous page"
          disabled={currentPage <= 1}
          onClick={onPrevious}
        >
          <LuChevronLeft size={16} />
        </PageNavButton>
        <span
          className="d-flex align-items-center gap-2"
          style={{ fontSize: "0.8125rem", color: "#0f1923" }}
        >
          Page
          <input
            type="number"
            className="page-jump-input"
            min={1}
            max={totalPages}
            value={pageInput}
            onChange={(e) => onPageInputChange(e.target.value)}
            onBlur={onCommitPageInput}
            onKeyDown={(e) => {
              if (e.key === "Enter") onCommitPageInput();
            }}
            style={{
              width: "48px",
              height: "32px",
              textAlign: "center",
              fontSize: "0.8125rem",
              border: "1px solid #cbd5e1",
              borderRadius: "6px",
            }}
          />
          of {totalPages}
        </span>
        <PageNavButton
          label="Next page"
          disabled={currentPage >= totalPages}
          onClick={onNext}
        >
          <LuChevronRight size={16} />
        </PageNavButton>
        <PageNavButton
          label="Last page"
          disabled={currentPage >= totalPages}
          onClick={onLast}
        >
          <LuChevronsRight size={16} />
        </PageNavButton>
      </div>
    </div>
  );
}
